import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Doc { id: number; series: string; number: string; issueDate: string; party: string; total: number; currency?: string }
interface Close {
  month: string;
  closed: { closedAt: string; closedBy: string | null; note: string } | null;
  closedMonths: string[];
  checks: {
    drafts: Doc[]; missingFiles: Doc[]; inbox: number;
    bank: { statements: number; coveredUntil: string | null; covered: boolean };
    unlinked: { id: number; bookingDate: string; amount: number; counterparty: string; purpose: string }[];
    forAccountant?: { id: number; bookingDate: string; amount: number; counterparty: string; purpose: string; note: string }[];
    declarations?: { obligation: string; title: string; due: string; filed: boolean; documents: number; paid?: number; expected?: number | null }[];
  };
  toPay: (Doc & { dueDate: string | null; overdue: boolean })[];
  toReceive: { count: number; total: number; overdue: number };
}

const shift = (month: string, by: number) => {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + by);
  return d.toISOString().slice(0, 7);
};
const label = (d: { series: string; number: string }) => `${d.series} ${d.number}`.trim();

/** D6 — Mėnesio uždarymas: the month's checklist, what to pay and to receive, and closing the month. */
export function ClosePage({ me }: { me: Me }) {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const [month, setMonth] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const data = useQuery({ queryKey: ['close', month], queryFn: () => api.get<Close>(`api/v1/close${month ? `?month=${month}` : ''}`) });
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['close'] }) });
  const c = data.data;
  const current = c?.month ?? month;
  const title = current ? new Date(`${current}-01T00:00:00`).toLocaleDateString(locale, { month: 'long', year: 'numeric' }) : '';
  const thisMonth = new Date().toISOString().slice(0, 7);

  const open = c ? c.checks.drafts.length + c.checks.missingFiles.length + (c.checks.inbox ? 1 : 0) + c.checks.unlinked.length + (c.checks.bank.statements && !c.checks.bank.covered ? 1 : 0) + (c.checks.declarations ?? []).filter((d) => !d.filed).length : 0;
  const requestInvoice = (u: Close['checks']['unlinked'][number]) => `mailto:?subject=${encodeURIComponent(t('Prašome atsiųsti sąskaitą faktūrą'))}&body=${encodeURIComponent(
    [t('Laba diena,'), t('{date} sumokėjome {amount} ({purpose}), tačiau sąskaitos faktūros negavome. Prašome ją atsiųsti.', { date: u.bookingDate, amount: money(Math.abs(u.amount)), purpose: u.purpose || u.counterparty }), t('Ačiū.')].join('\n\n'),
  )}`;

  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Mėnesio uždarymas')} title={title} text={t('Prieš uždarydami mėnesį patikrinkite, ar viskas sutvarkyta.')}
      action={current ? (
        <div className="close-nav">
          <button type="button" className="button button--ghost button--small" aria-label={t('Ankstesnis mėnuo')} onClick={() => setMonth(shift(current, -1))}>‹</button>
          <input type="month" className="month-input" value={current} max={thisMonth} onChange={(e) => { if (e.target.value) setMonth(e.target.value); }} aria-label={t('Mėnuo')} />
          <button type="button" className="button button--ghost button--small" aria-label={t('Kitas mėnuo')} disabled={current >= thisMonth} onClick={() => setMonth(shift(current, 1))}>›</button>
        </div>
      ) : null} />}>
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
      {c ? <>
        <section className={`panel-card close-status${c.closed ? ' is-closed' : ''}`}>
          {c.closed ? <>
            <div><p className="eyebrow">{t('Mėnuo uždarytas')}</p>
              <h2>{t('Uždaryta {date}', { date: new Date(c.closed.closedAt).toLocaleDateString(locale) })}{c.closed.closedBy ? ` · ${c.closed.closedBy}` : ''}</h2>
              {c.closed.note ? <p className="muted">{c.closed.note}</p> : null}
              {open ? <p className="close-warn">{t('Po uždarymo atsirado neužbaigtų dalykų: {n}.', { n: open })}</p> : null}
            </div>
            <button type="button" className="button button--ghost" onClick={() => act.mutate(() => api.delete(`api/v1/close/${c.month}`))}>{t('Atidaryti iš naujo')}</button>
          </> : <>
            <div><p className="eyebrow">{open ? t('Liko sutvarkyti: {n}', { n: open }) : t('Viskas sutvarkyta')}</p>
              <h2>{open ? t('Mėnuo dar neuždarytas') : t('Galite uždaryti mėnesį')}</h2>
              <textarea className="close-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Pastaba (nebūtina)')} />
            </div>
            <button type="button" className={`button ${open ? 'button--ghost' : 'button--primary'}`} disabled={act.isPending}
              onClick={() => { if (!open || confirm(t('Liko neužbaigtų dalykų. Vis tiek uždaryti mėnesį?'))) act.mutate(() => api.post('api/v1/close', { month: c.month, note })); }}>{t('Uždaryti mėnesį')}</button>
          </>}
        </section>

        <div className="close-layout">
          <section className="panel-card">
            <h2>{t('Patikra')}</h2>
            <ul className="close-checks">
              <Check ok={!c.checks.drafts.length} title={c.checks.drafts.length ? t('Neišsiųsti juodraščiai: {n}', { n: c.checks.drafts.length }) : t('Visos sąskaitos išrašytos')}>
                {c.checks.drafts.map((d) => <li key={d.id}><Link to="/outgoing/$id" params={{ id: String(d.id) }}>{label(d)}</Link> · {d.party} · {money(d.total, d.currency ?? 'EUR')}</li>)}
              </Check>
              <Check ok={!c.checks.missingFiles.length} title={c.checks.missingFiles.length ? t('Gautos sąskaitos be dokumento: {n}', { n: c.checks.missingFiles.length }) : t('Visų gautų sąskaitų dokumentai įkelti')}>
                {c.checks.missingFiles.map((d) => <li key={d.id}><Link to="/incoming/$id" params={{ id: String(d.id) }}>{label(d)}</Link> · {d.party} · {money(d.total)}</li>)}
              </Check>
              <Check ok={!c.checks.inbox} title={c.checks.inbox ? t('Neperžiūrėti dokumentai el. pašte: {n}', { n: c.checks.inbox }) : t('El. pašto dokumentai peržiūrėti')}>
                {c.checks.inbox ? <li><Link to="/invoices" search={{ tab: 'inbox' }}>{t('Atidaryti Gautas')}</Link></li> : null}
              </Check>
              <Check ok={c.checks.bank.covered} neutral={!c.checks.bank.statements}
                title={c.checks.bank.covered ? t('Banko išrašas įkeltas iki {date}', { date: c.checks.bank.coveredUntil ?? '' }) : c.checks.bank.statements ? t('Įkelkite banko išrašą iki mėnesio pabaigos') : t('Banko išrašų dar nekėlėte (nebūtina)')}>
                {!c.checks.bank.covered ? <li><Link to="/bank">{t('Atidaryti Banką')}</Link></li> : null}
              </Check>
              <Check ok={!c.checks.unlinked.length} neutral={!c.checks.bank.statements} title={c.checks.unlinked.length ? t('Nesusieti mokėjimai: {n}', { n: c.checks.unlinked.length }) : t('Visi mėnesio mokėjimai susieti')}>
                {c.checks.unlinked.map((u) => (
                  <li key={u.id}>
                    {u.bookingDate} · {u.counterparty || '—'} · <strong className={u.amount > 0 ? 'is-in' : ''}>{u.amount > 0 ? '+' : '−'}{money(Math.abs(u.amount))}</strong>
                    {' · '}<Link to="/bank">{t('Susieti mokėjimą')}</Link>
                    {u.amount < 0 ? <> · <a href={requestInvoice(u)}>{t('Prašyti sąskaitos')}</a></> : null}
                  </li>
                ))}
              </Check>
              {c.checks.forAccountant?.length ? (
                <Check ok={false} neutral title={t('Perduota buhalteriui sutvarkyti: {n}', { n: c.checks.forAccountant.length })}>
                  {c.checks.forAccountant.map((u) => <li key={u.id}>{u.bookingDate} · {u.counterparty || '—'} · {money(Math.abs(u.amount))}{u.note ? ` · ${u.note}` : ''}</li>)}
                </Check>
              ) : null}
              {c.checks.declarations?.length ? (() => {
                const missing = c.checks.declarations.filter((d) => !d.filed);
                return (
                  <Check ok={!missing.length} title={missing.length ? t('Nepateiktos (neįkeltos) deklaracijos: {n}', { n: missing.length }) : t('Mėnesio deklaracijos pateiktos')}>
                    {c.checks.declarations.map((d) => (
                      <li key={d.obligation}>{d.filed ? '✓' : '○'} {t(d.title)} · {t('iki {date}', { date: d.due })}
                        {d.paid !== undefined ? ` · ${t('sumokėta {sum}', { sum: money(d.paid) })}${d.expected ? ` / ${money(d.expected)}` : ''}` : ''}
                        {!d.filed ? <> · <Link to="/taxes">{t('Įkelti deklaraciją')}</Link></> : null}</li>
                    ))}
                  </Check>
                );
              })() : null}
            </ul>
          </section>

          <aside className="close-side">
            <section className="panel-card">
              <p className="eyebrow">{t('Mokėti iki mėnesio pabaigos')}</p>
              <h2>{money(c.toPay.reduce((s, d) => s + d.total, 0))}</h2>
              {c.toPay.length ? <ul className="close-pay">
                {c.toPay.map((d) => (
                  <li key={d.id} className={d.overdue ? 'is-late' : ''}>
                    <Link to="/incoming/$id" params={{ id: String(d.id) }}>{d.party || label(d)}</Link>
                    <small>{label(d)}{d.dueDate ? ` · ${t('iki')} ${d.dueDate}` : ''}</small>
                    <strong>{money(d.total)}</strong>
                  </li>
                ))}
              </ul> : <p className="muted">{t('Neapmokėtų gautų sąskaitų nėra.')}</p>}
            </section>
            <section className="panel-card">
              <p className="eyebrow">{t('Laukiama iš klientų')}</p>
              <h2>{money(c.toReceive.total)}</h2>
              <p className="muted">{t('{n} sąsk.', { n: c.toReceive.count })}{c.toReceive.overdue ? ` · ${t('vėluoja')} ${money(c.toReceive.overdue)}` : ''}</p>
              <Link to="/invoices" search={{ tab: 'out', view: 'unpaid' }} className="text-link">{t('Atidaryti neapmokėtas')}</Link>
            </section>
          </aside>
        </div>

        {c.closedMonths.length ? (
          <p className="muted close-history">{t('Uždaryti mėnesiai')}: {c.closedMonths.map((m, i) => <span key={m}>{i ? ', ' : ''}<button type="button" className="text-link" onClick={() => setMonth(m)}>{m}</button></span>)}</p>
        ) : null}
      </> : null}
    </PanelShell>
  );
}

function Check({ ok, neutral, title, children }: { ok: boolean; neutral?: boolean; title: string; children?: ReactNode }) {
  const state = ok ? 'is-ok' : neutral ? 'is-neutral' : 'is-open';
  return (
    <li className={`close-check ${state}`}>
      <span className="close-mark" aria-hidden="true">{ok ? '✓' : neutral ? '–' : '!'}</span>
      <div><strong>{title}</strong>{children ? <ul>{children}</ul> : null}</div>
    </li>
  );
}
